<script setup name="TvbDragBox">
import { ref, computed, onMounted, watch } from 'vue'
import { useElementVisibility } from '@vueuse/core'
const dragBoxRef = ref(null)
const offsetX = ref(0)
const offsetY = ref(0)
const isDragging = ref(false)
let startX = 0
let startY = 0
const STORAGE_KEY = 'tvb-drag-box-position'

const target = computed(() => {
  return dragBoxRef.value?.parentElement || dragBoxRef.value
})

const isNotFixed = useElementVisibility(target)

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  fixedWidth: {
    type: Number,
    default: 0
  },
  fixedHeight: {
    type: Number,
    default: 0
  }
})

const loadPositionFromStorage = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      const position = JSON.parse(stored)
      if (position.x !== undefined && position.y !== undefined) {
        offsetX.value = position.x
        offsetY.value = position.y
        return true
      }
    }
  } catch (error) {
    console.error('加载位置失败:', error)
  }
  return false
}

const savePositionToStorage = () => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        x: offsetX.value,
        y: offsetY.value
      })
    )
  } catch (error) {
    console.error('保存位置失败:', error)
  }
}

const initDefaultPosition = () => {
  if (!dragBoxRef.value) return

  if (!isNotFixed.value) {
    const hasStoredPosition = loadPositionFromStorage()

    if (!hasStoredPosition) {
      // 小窗初始位置（对齐 TC-1482 #4 设计稿）：基于原播放器容器右下角内侧定位，
      // 使小窗出现时贴在播放器右下边缘，而不是视口右下角
      const boxWidth = props.fixedWidth || dragBoxRef.value.offsetWidth
      const boxHeight = props.fixedHeight || dragBoxRef.value.offsetHeight
      const rect = target.value?.getBoundingClientRect()

      if (rect?.width) {
        // 播放器可见：小窗置于播放器右下角内侧（各留 16px 边距）
        // getBoundingClientRect 返回视口坐标，与 fixed 定位的 left/top 一致，无需加滚动偏移
        offsetX.value = Math.round(rect.right - boxWidth - 16)
        offsetY.value = Math.round(rect.bottom - boxHeight - 16)
      } else {
        // 兜底：视口右下角定位
        offsetX.value = window.innerWidth - boxWidth - 32
        offsetY.value = window.innerHeight - boxHeight - 16
      }

      // 初始位置需限制在当前视口范围内，避免小窗落在视口外
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      offsetX.value = Math.max(0, Math.min(offsetX.value, windowWidth - boxWidth))
      offsetY.value = Math.max(0, Math.min(offsetY.value, windowHeight - boxHeight))
    }
  }
}

const handleMouseDown = (e) => {
  if (!dragBoxRef.value || isNotFixed.value) return

  e.preventDefault()
  isDragging.value = true

  startX = e.clientX - offsetX.value
  startY = e.clientY - offsetY.value

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

const handleMouseMove = (e) => {
  if (!isDragging.value || !dragBoxRef.value) return

  offsetX.value = e.clientX - startX
  offsetY.value = e.clientY - startY

  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  const boxWidth = props.fixedWidth || dragBoxRef.value.offsetWidth
  const boxHeight = props.fixedHeight || dragBoxRef.value.offsetHeight

  offsetX.value = Math.max(0, Math.min(offsetX.value, windowWidth - boxWidth))
  offsetY.value = Math.max(0, Math.min(offsetY.value, windowHeight - boxHeight))
}

const handleMouseUp = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)

  savePositionToStorage()
}

const style = computed(() => {
  if (!isNotFixed.value && !props.disabled) {
    return {
      width: `${props.fixedWidth}px`,
      height: `${props.fixedHeight}px`,
      position: 'fixed',
      zIndex: '9999',
      left: `${offsetX.value}px`,
      top: `${offsetY.value}px`,
      cursor: 'move',
      // 小窗圆角 24px → 16px（对齐设计稿 TC-1481 #14）
      borderRadius: '16px',
      overflow: 'hidden'
    }
  } else {
    return {}
  }
})

watch(isNotFixed, () => {
  initDefaultPosition()
})

onMounted(() => {
  initDefaultPosition()
})
</script>

<template>
  <div
    class="tvb-drag-box"
    :class="{ 'tvb-drag-box-fixed': !isNotFixed && !disabled }"
    :style="style"
    ref="dragBoxRef"
    @mousedown="handleMouseDown"
  >
    <slot></slot>
  </div>
</template>

<style lang="scss" scoped>
.tvb-drag-box {
  transition: none;

  &.tvb-drag-box-fixed {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0);
      z-index: 999;
    }
  }
}
</style>
